import moment from 'moment';

import { Injectable } from '@angular/core';
import { Subject }    from 'rxjs';

import { ApiService }     from '../api/api.service';
import { CompanyService } from '../companies/company.service';
import { IntelligenceCustomersModel }       from './typings/intelligence.customers';
import { IntelligenceDealershipStatsModel } from './typings/intelligence.dealership-stats';
import { IntelligenceFinancialsModel }      from './typings/intelligence.financials';
import { IntelligenceInspectionStatsModel } from './typings/intelligence.inspection-stats';
import { IntelligenceMechanicJobModel }     from './typings/intelligence.mechanic-job';
import { IntelligenceMechanicsModel }       from './typings/intelligence.mechanics';
import { IntelligenceOperationsModel }      from './typings/intelligence.operations';
import { IntelligenceProductsModel }        from './typings/intelligence.products';
import { IntelligenceRangeModel }           from './typings/intelligence.range';
import { IntelligenceSummaryModel }         from './typings/intelligence.summary';
import { IntelligenceVehiclesModel }        from './typings/intelligence.vehicles';

@Injectable({
  providedIn: 'root'
})
export class IntelligenceService {
  range : IntelligenceRangeModel = this.presetRange('LAST_30');

  rangeChanged = new Subject<IntelligenceRangeModel>();

  constructor (
    private apiService    : ApiService,
    private companyService: CompanyService
  ) {}

  getCustomers () : Promise<IntelligenceCustomersModel> {
    return this.apiService.get(`/intelligence/customers?${ this.rangeQuery() }`);
  }

  getFinancials () : Promise<IntelligenceFinancialsModel> {
    return this.apiService.get(`/intelligence/financials?${ this.rangeQuery() }`);
  }

  getDealershipStats () : Promise<IntelligenceDealershipStatsModel> {
    return this.apiService.get('/dashboard/dealership_widget_stats');
  }

  getInspectionStats () : Promise<IntelligenceInspectionStatsModel> {
    return this.apiService.get('/dashboard/inspection_widget_stats');
  }

  getMechanicJobs ( mechanicId : string ) : Promise<{ jobs : Array<IntelligenceMechanicJobModel> }> {
    return this.apiService.get(`/intelligence/mechanics/${ mechanicId }/jobs?${ this.rangeQuery() }`);
  }

  getMechanics () : Promise<IntelligenceMechanicsModel> {
    return this.apiService.get(`/intelligence/mechanics?${ this.rangeQuery() }`);
  }

  getOperations () : Promise<IntelligenceOperationsModel> {
    return this.apiService.get(`/intelligence/operations?${ this.rangeQuery() }`);
  }

  getProducts () : Promise<IntelligenceProductsModel> {
    return this.apiService.get(`/intelligence/products?${ this.rangeQuery() }`);
  }

  getSummary () : Promise<IntelligenceSummaryModel> {
    return this.apiService.get(`/intelligence/summary?${ this.rangeQuery() }`);
  }

  getVehicles () : Promise<IntelligenceVehiclesModel> {
    return this.apiService.get(`/intelligence/vehicles?${ this.rangeQuery() }`);
  }

  presetRange ( preset : string ) : IntelligenceRangeModel {
    const today = this.companyToday();
    const end = this.toIso(today);

    switch (preset) {
      case 'TODAY':
        return { end_date: end, start_date: end };
      case 'THIS_WEEK':
        return { end_date: end, start_date: this.toIso(this.startOfWeek(today)) };
      case 'LAST_WEEK':
        return {
          end_date  : this.toIso(this.addDays(this.startOfWeek(today), -1)),
          start_date: this.toIso(this.addDays(this.startOfWeek(today), -7))
        };
      case 'THIS_MONTH':
        return { end_date: end, start_date: this.toIso(new Date(today.getFullYear(), today.getMonth(), 1)) };
      case 'LAST_MONTH':
        return {
          end_date  : this.toIso(new Date(today.getFullYear(), today.getMonth(), 0)),
          start_date: this.toIso(new Date(today.getFullYear(), today.getMonth() - 1, 1))
        };
      case 'YTD':
        return { end_date: end, start_date: this.toIso(new Date(today.getFullYear(), 0, 1)) };
      default:
        return { end_date: end, start_date: this.toIso(this.addDays(today, -29)) };
    }
  }

  setRange ( range : IntelligenceRangeModel ) : void {
    this.range = range;

    this.rangeChanged.next(range);
  }

  private addDays ( date : Date, days : number ) : Date {
    const copy = new Date(date.getTime());

    copy.setDate(copy.getDate() + days);

    return copy;
  }

  private companyToday () : Date {
    const now = moment().utcOffset(this.companyService.getUtcOffset());

    return new Date(now.year(), now.month(), now.date());
  }

  private rangeQuery () : string {
    return `start_date=${ this.range.start_date }&end_date=${ this.range.end_date }`;
  }

  private startOfWeek ( date : Date ) : Date {
    const copy = new Date(date.getTime());

    copy.setDate(copy.getDate() - ( ( copy.getDay() + 6 ) % 7 ));

    return copy;
  }

  private toIso ( date : Date ) : string {
    const month = `0${ date.getMonth() + 1 }`.slice(-2);
    const day = `0${ date.getDate() }`.slice(-2);

    return `${ date.getFullYear() }-${ month }-${ day }`;
  }
}
